// 用户模块状态管理

import { createWithEqualityFn } from "zustand/traditional";
import { subscribeWithSelector } from "zustand/middleware";
import { shallow } from "zustand/shallow";
import { UserState, UserActions, User } from "../types";
import { LoginResponse, RegisterResponse } from "../types/auth.types";
import { userService } from "../services";
import { BaseCache } from "../../base/stores";
import { utils } from "../../..";

// 创建用户模块专用的缓存实例
const userCache = new BaseCache({
  defaultTTL: 3 * 60 * 1000, // 3分钟
  maxSize: 30,
  enableLogging: true,
});

// 初始状态 - 从本地存储恢复用户信息
const getInitialUserInfo = (): User | null => {
  try {
    const cachedUserInfo = utils.getUser();
    return cachedUserInfo ? JSON.parse(cachedUserInfo) : null;
  } catch (error) {
    console.warn("parse local storage user info failed:", error);
    return null;
  }
};

const initialState: UserState = {
  userInfo: getInitialUserInfo(),
  userLoading: false,
  userError: null,
  loginLoading: false,
  registerLoading: false,
  loginError: null,
  registerError: null,
  unpaidOrders: [],
  cache: new Map(),
};

// 创建用户store
export const useUserStore = createWithEqualityFn<UserState & UserActions>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    setUserInfo: data => {
      if (data) {
        set({ userInfo: data });
      } else {
        const cachedUserInfo = utils.getUser();
        set({
          userInfo: cachedUserInfo ? JSON.parse(cachedUserInfo) : null,
        });
      }
    },
    setUserLoading: loading => set({ userLoading: loading }),
    setUserError: error => set({ userError: error }),
    setLoginLoading: loading => set({ loginLoading: loading }),
    setLoginError: error => set({ loginError: error }),
    setRegisterLoading: loading => set({ registerLoading: loading }),
    setRegisterError: error => set({ registerError: error }),
    loadUserInfo: async () => {
      const { setUserLoading, setUserError, setUserInfo } = get();
      setUserLoading(true);
      setUserError(null);
      try {
        const user = await userService.loadUserInfo();
        setUserInfo(user);
        utils.setUser(user);
        setUserLoading(false);

        return user;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "load user info failed";
        setUserError(errorMessage);
        setUserLoading(false);
        throw error;
      }
    },

    loadGuestInfo: async () => {
      const { setUserLoading, setUserError, setUserInfo } = get();
      setUserLoading(true);
      setUserError(null);
      try {
        const user = await userService.loadGuestInfo();
        setUserInfo(user);
        utils.setUser(user);
        setUserLoading(false);
        return user;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "load guest info failed";
        setUserError(errorMessage);
        setUserLoading(false);
        throw error;
      }
    },
    updateUserBalance: balance =>
      set(state => ({
        userInfo: state.userInfo ? { ...state.userInfo, balance } : null,
      })),
    refreshUserInfo: () => set({ userInfo: get().userInfo }),
    isAuthenticated: () => get().userInfo !== null && !!get().userInfo?.userId,
    logout: () => {
      userService.logout();
    },

    setCache: (key, data, ttl) => {
      userCache.setCache(key, data, ttl);
      set({ cache: new Map(userCache["cache"]) });
    },

    getCache: key => {
      return userCache.getCache(key);
    },

    clearCache: pattern => {
      userCache.clearCache(pattern);
      set({ cache: new Map(userCache["cache"]) });
    },

    hasCache: key => {
      return userCache.hasCache(key);
    },

    getCacheKeys: () => {
      return userCache.getCacheKeys();
    },

    getCacheSize: () => {
      return userCache.getCacheSize();
    },

    cacheFirst: async (key, apiCall, ttl) => {
      return userCache.cacheFirst(key, apiCall, ttl);
    },
    login: async data => {
      const { setLoginLoading, setLoginError, setUserInfo } = get();
      setLoginLoading(true);
      setLoginError(null);
      try {
        const result: LoginResponse = await userService.login(data);
        setUserInfo(result.user);
        utils.setUser(result.user);
        setLoginLoading(false);
        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "login failed";
        setLoginError(errorMessage);
        setLoginLoading(false);
        throw error;
      }
    },
    register: async data => {
      const { setRegisterLoading, setRegisterError, setUserInfo } = get();
      setRegisterLoading(true);
      setRegisterError(null);
      try {
        const result: RegisterResponse = await userService.register(data);
        setUserInfo(result.user);
        utils.setUser(result.user);
        setRegisterLoading(false);
        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "register failed";
        setRegisterError(errorMessage);
        setRegisterLoading(false);
        throw error;
      }
    },
  }))
);

// 选择器hooks
export const useUserData = () =>
  useUserStore(state => ({
    data: state.userInfo,
    loading: state.userLoading,
    error: state.userError,
  }));

export const useUserAuth = () =>
  useUserStore(state => ({
    isAuthenticated: state.isAuthenticated(),
    loginLoading: state.loginLoading,
    registerLoading: state.registerLoading,
    loadUserInfo: state.loadUserInfo,
    loadGuestInfo: state.loadGuestInfo,
    loginError: state.loginError,
    registerError: state.registerError,
    login: state.login,
    register: state.register,
    logout: state.logout,
  }));

// 扁平化的用户数据 hook - 直接导出 User 的所有属性并整合认证功能
export const useUserDataFlattened = () =>
  useUserStore(state => {
    const {
      userInfo,
      userLoading,
      userError,
      loginLoading,
      registerLoading,
      loginError,
      registerError,
    } = state;

    // 如果 userInfo 为 null，返回所有属性为 undefined
    if (!userInfo) {
      return {
        // 状态信息
        loading: userLoading,
        error: userError,
        data: null,

        // 认证相关状态和方法
        isAuthenticated: false,
        loginLoading,
        registerLoading,
        loginError,
        registerError,

        // 认证相关方法
        loadUserInfo: state.loadUserInfo,
        login: state.login,
        register: state.register,
        logout: state.logout,
        updateUserBalance: state.updateUserBalance,
        refreshUserInfo: state.refreshUserInfo,

        // User 的所有属性，当 userInfo 为 null 时都设为 undefined
        userId: undefined,
        username: undefined,
        root: undefined,
        big: undefined,
        center: undefined,
        showEncryptData: undefined,
        out: undefined,
        token: undefined,
        selB: undefined,
        currRatio: undefined,
        currSymbol: undefined,
        createdTime: undefined,
        auth2Verify: undefined,
        email: undefined,
        phone: undefined,
        phoneChecked: undefined,
        avatar: undefined,
        balance: undefined,
        taskBonusBalance: undefined,
        depositForWithdraw: undefined,
        registerTaskBonusBalance: undefined,
        cumulativeBetAmount: undefined,
        firstRechargeFlag: undefined,
        thirdGameWalletList: undefined,
        vipLevel: undefined,
        nextlevel: undefined,
        thirdLoginTypes: undefined,
        bind2Auth: undefined,
        firstRechargeRuleAmount: undefined,
        customRechargeRules: undefined,
        redpacket: undefined,
        betAmount: undefined,
        rechargeAmount: undefined,
        withdrawAmount: undefined,
        bonusTaskCheckInDay: undefined,
        todayCheckIn: undefined,
        upgraded: undefined,
        noticeUpdateTime: undefined,
        customRechargeRuleAmount: undefined,
        whatsapp: undefined,
        preferences: undefined,
        userCheckIn: undefined,
        redPacketReceivable: undefined,
        rechargeCount: undefined,
        dailyRechargeTasks: undefined,
        dailyRechargeAllCompleted: undefined,
        dailyRechargeTask: undefined,
        dailyBetTask: undefined,
      };
    }

    // 当 userInfo 存在时，展开所有属性
    return {
      // 状态信息
      loading: userLoading,
      error: userError,
      data: userInfo,

      // 认证相关状态和方法
      isAuthenticated: !!userInfo.userId,
      loginLoading,
      registerLoading,
      loginError,
      registerError,

      // 认证相关方法
      loadUserInfo: state.loadUserInfo,
      login: state.login,
      register: state.register,
      logout: state.logout,
      updateUserBalance: state.updateUserBalance,
      refreshUserInfo: state.refreshUserInfo,

      // User 的所有属性
      ...userInfo,
    };
  }, shallow);

export function useUserCache() {
  const { cache } = useUserStore(state => ({
    cache: state.cache,
  }));
  return cache;
}
